/**
 * Shared countdown custom element used by announcement-bar and accessories-sales.
 */
class AnnouncementCountdown extends HTMLElement {
  static SITE_TIMEZONES = {
    us: "America/New_York",
    uk: "Europe/London",
    de: "Europe/Berlin",
    test: "America/New_York",
  };

  connectedCallback() {
    // accessories-sales 会把自身挪到选购区容器，disconnect 后需重新启动计时
    if (this._interval) return;

    this.timeZone = this.resolveTimeZone();
    this.date = this.parseDate(this.dataset.date, this.timeZone);
    this.baseFormat = this.dataset.format || "hms";
    this.format = this.baseFormat;
    this.autoHms = this.dataset.autoHms === "true";
    this.autoHmsDays = Math.max(1, parseInt(this.dataset.autoHmsDays, 10) || 2);
    this.fixedBoxSize = !!(this.closest && this.closest(".accessories-sales-notice"));

    if (isNaN(this.date)) {
      this.hide();
      return;
    }

    this.daysEl = this.querySelector("[data-countdown-days]");
    this.hoursEl = this.querySelector("[data-countdown-hours]");
    this.minutesEl = this.querySelector("[data-countdown-minutes]");
    this.secondsEl = this.querySelector("[data-countdown-seconds]");
    this.daysParts = this.querySelectorAll('[data-countdown-part="days"]');
    this.secondsParts = this.querySelectorAll('[data-countdown-part="seconds"]');

    this.daysPad = 2;
    this.hoursPad = 2;
    this._formatPartsReady = false;
    this.applyFormatForDiff(this.date - Date.now());
    this.lockDigitsFromDiff(this.date - Date.now());

    this.tick();
    this.lockContainerWidth();
    this._interval = setInterval(this.tick.bind(this), 1000);
  }

  disconnectedCallback() {
    if (this._interval) {
      clearInterval(this._interval);
      this._interval = null;
    }
  }

  resolveTimeZone() {
    if (this.dataset.timezone) return this.dataset.timezone;

    const shopKey = String(
      window.__vernal?.shopKey || window.shop_country || ""
    ).toLowerCase();

    return (
      AnnouncementCountdown.SITE_TIMEZONES[shopKey] ||
      AnnouncementCountdown.SITE_TIMEZONES.us
    );
  }

  /**
   * 将运营配置的「站点当地墙钟时间」转为 UTC 时间戳。
   * 忽略字符串里的 Z/偏移，统一按站点时区解释，避免美国多时区访客结束时刻不一致。
   */
  parseDate(dateStr, timeZone) {
    if (!dateStr) return NaN;

    const raw = String(dateStr).trim();
    const withoutTz = raw
      .replace(/[zZ]$/, "")
      .replace(/[+-]\d{2}:?\d{2}$/, "")
      .trim();

    const zoned = this.parseZonedDateTime(withoutTz, timeZone);
    if (!isNaN(zoned)) return zoned;

    let ts = Date.parse(raw);
    if (!isNaN(ts)) return ts;
    return Date.parse(raw.replace(/-/g, "/"));
  }

  parseZonedDateTime(dateStr, timeZone) {
    if (!dateStr || !timeZone) return NaN;

    const cleaned = String(dateStr).replace(/\//g, "-").replace(" ", "T");
    const match = cleaned.match(
      /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2})(?::(\d{2})(?::(\d{2}))?)?)?/
    );
    if (!match) return NaN;

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const hour = Number(match[4] || 0);
    const minute = Number(match[5] || 0);
    const second = Number(match[6] || 0);

    const desiredAsUtc = Date.UTC(year, month - 1, day, hour, minute, second);
    let utcMs = desiredAsUtc;

    for (let i = 0; i < 3; i++) {
      const parts = this.getTimeZoneParts(utcMs, timeZone);
      if (!parts) return NaN;

      const observedAsUtc = Date.UTC(
        parts.year,
        parts.month - 1,
        parts.day,
        parts.hour,
        parts.minute,
        parts.second
      );
      const diff = observedAsUtc - desiredAsUtc;
      utcMs -= diff;
      if (diff === 0) break;
    }

    return utcMs;
  }

  getTimeZoneParts(utcMs, timeZone) {
    try {
      const dtf = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
      });

      const map = {};
      for (const { type, value } of dtf.formatToParts(new Date(utcMs))) {
        if (type !== "literal") map[type] = value;
      }

      let hour = Number(map.hour);
      if (hour === 24) hour = 0;

      return {
        year: Number(map.year),
        month: Number(map.month),
        day: Number(map.day),
        hour,
        minute: Number(map.minute),
        second: Number(map.second),
      };
    } catch (e) {
      return null;
    }
  }

  formatPart(n) {
    return n < 10 ? "0" + n : String(n);
  }

  /**
   * DHM 格式且开启自动切换时：剩余完整天数小于阈值则切到 HMS。
   */
  resolveFormat(diff) {
    if (
      this.baseFormat === "dhm" &&
      this.autoHms &&
      diff >= 0 &&
      Math.floor(diff / 86400000) < this.autoHmsDays
    ) {
      return "hms";
    }
    return this.baseFormat;
  }

  applyFormatForDiff(diff) {
    const nextFormat = this.resolveFormat(diff);
    if (nextFormat === this.format && this._formatPartsReady) return;

    this.format = nextFormat;
    this._formatPartsReady = true;

    const showDays = this.format === "dhm" || this.format === "dhms";
    const showSeconds = this.format === "hms" || this.format === "dhms";

    this.toggleParts(this.daysParts, showDays);
    this.toggleParts(this.secondsParts, showSeconds);
    this.setAttribute("data-active-format", this.format);
  }

  toggleParts(nodes, visible) {
    if (!nodes || !nodes.length) return;
    nodes.forEach((el) => {
      el.hidden = !visible;
      el.classList.toggle("is-hidden", !visible);
    });
  }

  lockDigitsFromDiff(diff) {
    if (diff < 0 || this.fixedBoxSize) return;

    const dayMs = 86400000;
    const hourMs = 3600000;
    let days = Math.floor(diff / dayMs);
    let hours = Math.floor((diff % dayMs) / hourMs);

    if (this.format === "hms") {
      hours = Math.floor(diff / hourMs);
      days = 0;
    }

    this.daysPad = Math.max(2, String(days).length);
    this.hoursPad = Math.max(2, String(hours).length);

    const isBoxed = !!(this.closest && this.closest(".announcement-countdown--boxed"));

    this.lockValueWidth(this.daysEl, this.daysPad, isBoxed);
    this.lockValueWidth(this.hoursEl, this.hoursPad, isBoxed);
    this.lockValueWidth(this.minutesEl, 2, isBoxed);
    this.lockValueWidth(this.secondsEl, 2, isBoxed);
  }

  lockValueWidth(el, digits, isBoxed) {
    if (!el || this.fixedBoxSize) return;

    const valueWidth = `${digits * 0.65}em`;

    if (isBoxed) {
      el.style.width = valueWidth;
      el.style.minWidth = valueWidth;
      const unit = el.closest(".announcement-countdown__unit");
      if (unit) unit.style.minWidth = `calc(${valueWidth} + 1.5em)`;
      return;
    }

    el.style.minWidth = valueWidth;
  }

  lockContainerWidth() {
    if (this.fixedBoxSize) return;

    const wrap = this.closest(".announcement-countdown");
    if (!wrap) return;

    requestAnimationFrame(() => {
      const width = Math.ceil(wrap.getBoundingClientRect().width);
      if (width > 0) wrap.style.minWidth = `${width}px`;
    });
  }

  tick() {
    const diff = this.date - Date.now();
    if (diff < 0) {
      clearInterval(this._interval);
      this.hide();
      return;
    }

    const prevFormat = this.format;
    this.applyFormatForDiff(diff);
    if (prevFormat !== this.format) {
      this.lockDigitsFromDiff(diff);
      this.lockContainerWidth();
    }

    const dayMs = 86400000;
    const hourMs = 3600000;
    let days = Math.floor(diff / dayMs);
    let hours = Math.floor((diff % dayMs) / hourMs);
    const mins = Math.floor((diff % hourMs) / 60000);
    const secs = Math.floor((diff % 60000) / 1000);

    if (this.format === "hms") {
      hours = Math.floor(diff / hourMs);
      days = 0;
    }

    this.updateEl(this.daysEl, this.formatPart(days));
    this.updateEl(this.hoursEl, this.formatPart(hours));
    this.updateEl(this.minutesEl, this.formatPart(mins));
    this.updateEl(this.secondsEl, this.formatPart(secs));
  }

  updateEl(el, value) {
    if (!el || el.textContent === value) return;
    el.textContent = value;
  }

  hide() {
    const wrap = this.closest(".announcement-countdown");
    if (wrap) wrap.classList.add("is-ended");
    this.classList.add("is-ended");
  }
}

if (!customElements.get("announcement-countdown")) {
  customElements.define("announcement-countdown", AnnouncementCountdown);
}
